import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../context';
import { dayRangeParams, localISODate } from '../dates';

interface Summary {
  totals: {
    total: number; answered: number; missed: number;
    completed: number; called_back: number; avg_answer_seconds: number | null;
  };
  byNumber: { caller_number: string | null; calls: number; missed: number; last_call: string }[];
  byLine: { line: string; calls: number; missed: number; avg_answer_seconds: number | null }[];
  byAnswerer: { answered_by: string; calls: number; avg_answer_seconds: number | null }[];
}

const startOfWeekAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return localISODate(d);
};

/** Reporting: calls by number, line and answerer over a period, with CSV export. */
export function ReportsPage() {
  const api = useApi();
  const [from, setFrom] = useState(startOfWeekAgo());
  const [to, setTo] = useState(localISODate());
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const range = useCallback(() => dayRangeParams(from, to), [from, to]);

  useEffect(() => {
    api.get<Summary>(`/api/reports/summary?${range()}`).then(setData, (e: Error) => setError(e.message));
  }, [api, range]);

  async function exportCsv() {
    const rows = await api.get<Record<string, unknown>[]>(`/api/reports/calls?${range()}`);
    if (rows.length === 0) return;
    const cols = Object.keys(rows[0]!);
    // Quote everything and defuse spreadsheet formula evaluation (=, +, -, @, tab)
    // - caller-supplied strings must never execute when the CSV is opened in Excel.
    const esc = (v: unknown) => {
      let str = String(v ?? '');
      if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;
      return `"${str.replaceAll('"', '""')}"`;
    };
    const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `calls-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const secs = (s: number | null) => (s === null ? '—' : `${s}s`);

  return (
    <>
      <h1>Reports</h1>
      <div className="filters">
        <label>From <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>To <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
        <button onClick={exportCsv}>Export CSV</button>
      </div>
      {error && <p className="error">{error}</p>}
      {data && (
        <>
          <div className="stats">
            <div className="stat"><div className="num">{data.totals.total}</div>Calls</div>
            <div className="stat"><div className="num">{data.totals.answered}</div>Answered</div>
            <div className="stat"><div className="num">{data.totals.missed}</div>Missed</div>
            <div className="stat"><div className="num">{data.totals.completed}</div>Completed</div>
            <div className="stat"><div className="num">{data.totals.called_back}</div>Called back</div>
            <div className="stat"><div className="num">{secs(data.totals.avg_answer_seconds)}</div>Avg time to answer</div>
          </div>
          <h2>By caller number</h2>
          <table>
            <thead><tr><th>Number</th><th>Calls</th><th>Missed</th><th>Last call</th></tr></thead>
            <tbody>
              {data.byNumber.map((r) => (
                <tr key={r.caller_number ?? 'unknown'}>
                  <td>{r.caller_number ?? 'Unknown'}</td><td>{r.calls}</td><td>{r.missed}</td>
                  <td>{new Date(r.last_call).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h2>By line</h2>
          <table>
            <thead><tr><th>Line</th><th>Calls</th><th>Missed</th><th>Avg time to answer</th></tr></thead>
            <tbody>
              {data.byLine.map((r) => (
                <tr key={r.line}><td>{r.line}</td><td>{r.calls}</td><td>{r.missed}</td><td>{secs(r.avg_answer_seconds)}</td></tr>
              ))}
            </tbody>
          </table>
          <h2>By answerer</h2>
          <table>
            <thead><tr><th>Answered by</th><th>Calls</th><th>Avg time to answer</th></tr></thead>
            <tbody>
              {data.byAnswerer.map((r) => (
                <tr key={r.answered_by}><td>{r.answered_by}</td><td>{r.calls}</td><td>{secs(r.avg_answer_seconds)}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
